/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React, { useCallback } from 'react';
import { matchPath } from 'react-router';
import { getRootAppPath, rootAppPath } from './utils';
import { Route, Switch, useHistory } from 'react-router-dom';
import { reportRoutes } from './ReportingRoute';
import { AppRouteInterface, RouteInterface, RoutesInterface } from '../../types/routes';
import { CompanyTrackerInitiativeRoute, companyTrackerInitiativeRoutes } from './CompanyTrackerInitiativeRoute';
import RouteErrorBoundary from '../../components/error/RouteErrorBoundary';
import { useSelector } from 'react-redux';
import UTrModal from '../../components/utr-modal';
import Dashboard, { DashboardSection } from '../../components/dashboard';
import { getInitiativeTree } from '../../selectors/initiativeTree';
import { OrganizationSwitcherRoute } from '../OrganizationSwitcherRoute';
import { companyTrackerRouteMatcher } from '../appRootMatcher';
import RedirectToCompanyTracker from './RedirectToCompanyTracker';
import { getDefaultConfig } from '../../config/app-config';
import { AgreementChecker } from './partials/AgreementChecker';
import './styles.scss';

// Do not require initiative
export const companytrackerRouteEntry: RouteInterface = {
  id: 'company_tracker_root',
  label: 'Survey',
  tooltip: 'Survey',
  path: `/${rootAppPath}`,
  icon: 'fa-tasks',
  component: CompanyTracker,
  appPermissionId: 'app_company_tracker',
  exact: false,
  auth: true,
  getRootAppPath: getRootAppPath
};

const redirector = {
  COMPANY_TRACKER_SURVEY_REDIRECTOR: {
    id: 'company_tracker_survey_redirector',
    label: 'Survey Redirect',
    path: `/${rootAppPath}/redirect/:initiativeId/:destination(survey)`,
    defaultParams: {
      destination: 'survey'
    },
    component: RedirectToCompanyTracker,
    appPermissionId: 'app_company_tracker',
    auth: true,
    hideOnMissingPermission: true,
    requiresInitiativeId: true,
    parentId: 'company_tracker',
    getRootAppPath: getRootAppPath
  }
}

export const companyTrackerRoutes: RoutesInterface = {
  COMPANY_TRACKER: companytrackerRouteEntry,
  ...redirector,
  ...reportRoutes,
  ...companyTrackerInitiativeRoutes,
}

const branding = getDefaultConfig().branding.ctlApp;
const companyTrackerAppRoute: AppRouteInterface = {
  appName: branding.name,
  appIcon: branding.logo,
  routes: [
    reportRoutes.COMPANY_TRACKER_SURVEY,
    companyTrackerRoutes.COMPANY_TRACKER_LIST,
    companyTrackerRoutes.SUMMARY,
    companyTrackerRoutes.DOWNLOADS,
    companyTrackerRoutes.ADMIN_SETTINGS,
  ],
  hasReportingLevels: true,
}

const pathMatch = `${companytrackerRouteEntry.path}/:anyPage?/:initiativeId?`;

// Extract all routes that allow initiative change
const routesWithSwitch = Object.values(companyTrackerRoutes).filter(r => r.allowInitiativeChange);

/**
 * Company Tracker App entry point
 */
export function CompanyTracker() {

  const history = useHistory();

  const initiativeTree = useSelector(getInitiativeTree);

  const redirectRoute = routesWithSwitch.find(route => matchPath(history.location.pathname, route))
    ?? companyTrackerRoutes.SUMMARY;

  const redirectHandler = useCallback((initiativeId: string, rootAppPath?: string) => {
    if (initiativeId) {
      history.push(companyTrackerRouteMatcher.generateUrl(initiativeId, rootAppPath))
    }
  }, [history]);

  return (
    <RouteErrorBoundary>
      <OrganizationSwitcherRoute
        appRoute={companyTrackerAppRoute}
        routeMatcher={companyTrackerRouteMatcher}
        changeReportingLevelRoute={redirectRoute}
        redirectHandler={redirectHandler}
        initiativeTree={initiativeTree}
        pathMatch={pathMatch}>
        <AgreementChecker />
        <Switch>
          <Route path={redirector.COMPANY_TRACKER_SURVEY_REDIRECTOR.path}>
            <redirector.COMPANY_TRACKER_SURVEY_REDIRECTOR.component />
          </Route>

          {/*"/company-tracker/reports/:initiativeId?",*/}
          <Route {...reportRoutes.COMPANY_TRACKER_LIST} />
          <Route {...reportRoutes.COMPANY_TRACKER_SURVEY} />
          {/* Normal Initiative*/}
          <Route path={pathMatch} component={CompanyTrackerInitiativeRoute} />
          <Route>
            <Dashboard>
              <DashboardSection icon={'fa-tasks'} title='Company Tracker'>
                <p>You currently don't have any Companies associated with this account.</p>
                <p>Please contact our customer success team to set up your account.</p>
              </DashboardSection>
            </Dashboard>
          </Route>
        </Switch>
        <UTrModal />
      </OrganizationSwitcherRoute>
    </RouteErrorBoundary>
  )
}

export default CompanyTracker;
